import { createSlice, configureStore } from '@reduxjs/toolkit';

const initialVisualizerState = { 
    surfaceList : [],
    selectedSurface: {type:"FLOOR"},
    activeProduct: {},
    specifier:"",
    selString:"-1",

    appliedFilters:[],
    lastAppliedFilter:"",
    patternData:[],
    activePattern:null,
    matchingPatterns:[],
    selectedSlotProducts:{}, // Store products for each color slot
    selectedSlot:0, // Currently selected color slot index

    // History tracking for color selections
    colorSelectionHistory: [], // Array of selectedSlotProducts snapshots
    colorHistoryIndex: -1, // Current position in history

    //active Design mode
    applyMode:"flood",
    activeTile:-1,
    tileSelections:{},

    productDataBySeriesId:{},

    favorites:{},

    shareData:{
        specifier:"",
        sel:-1,
        url:"",
        message:"Check out this design I made using the Tarkett Contour Visualizer!"
    }
};

const visualizerSlice = createSlice({
  name: "visualizer",
  initialState: initialVisualizerState,
  reducers: {

    loadBackup(state, actions){
      state.activeTile = actions.payload.activeTile;
      state.tileSelections = actions.payload.tileSelections;
      state.activePattern = actions.payload.activePattern;
    },


    // Visualizer base states
    setSurfaceList(state, action) {
      state.surfaceList = action.payload;
    },

    setSpecifier(state, action) {
      state.specifier = action.payload;
    },

    setSelString(state, action) {
      state.selString = action.payload;
    },
    setActiveProduct(state, action) {
      state.activeProduct = action.payload;
    },

    //-------------------------------------

    // Applied filters state handlers
    addAppliedFilter(state, action) {
      var filter = action.payload;
      state.appliedFilters = [...state.appliedFilters, filter];
    },

    updateAppliedFilterByIndex(state, action) {
      state.appliedFilters[action.payload.index] = action.payload.filter;
    },

    removeAppliedFilter(state, action) {
      state.appliedFilters = state.appliedFilters.filter(
        (f) => !(f.name === action.payload.name && f.value === action.payload.value)
      );
    },

    setLastAppliedFilter(state, action) {
      state.lastAppliedFilter = action.payload;
    },

    removeAppliedFilterByGroupId(state, action) {
      state.appliedFilters = state.appliedFilters.filter(
        (f) => !(f.groupId === action.payload)
      );
    },

    clearAppliedFilterByName(state, action){
      state.appliedFilters = state.appliedFilters.filter(
        (f) => f.name !== action.payload.name
      );
    },

    resetAppliedFilters(state, action){
      state.appliedFilters = [];
    },

    //------------------------------------
    setPatternData(state, action){
      state.patternData = action.payload;
    }, 

    setActivePattern(state, action){
      state.activePattern = action.payload;
      
      // Initialize color selection history when pattern changes
      state.colorSelectionHistory = [{}];
      state.colorHistoryIndex = 0;
      state.selectedSlotProducts = {};
    },

    setMatchingPatterns(state, action){
      state.matchingPatterns = action.payload;
    },

    setSlotProduct(state, action){
      const { slotIndex, product } = action.payload;
      
      // Remove any history after current index (when making new changes after undo)
      state.colorSelectionHistory = state.colorSelectionHistory.slice(0, state.colorHistoryIndex + 1);
      
      // Apply the change first
      state.selectedSlotProducts[slotIndex] = product;
      
      // Save the NEW state to history after applying the change
      const newState = JSON.parse(JSON.stringify(state.selectedSlotProducts));
      state.colorSelectionHistory.push(newState);
      
      // Move index to the new state
      state.colorHistoryIndex++;
      
      // Limit history to 20 entries
      if (state.colorSelectionHistory.length > 20) {
        state.colorSelectionHistory.shift(); // Remove first item
        state.colorHistoryIndex--; // Adjust index
      }
    },

    setSelectedSlot(state, action){
      state.selectedSlot = action.payload;
    },

    clearAllSlots(state, action){
      // Remove any history after current index
      state.colorSelectionHistory = state.colorSelectionHistory.slice(0, state.colorHistoryIndex + 1);
      
      // Clear slots first
      state.selectedSlotProducts = {};
      state.selectedSlot = 0;
      
      // Save the cleared state to history
      const clearedState = JSON.parse(JSON.stringify(state.selectedSlotProducts));
      state.colorSelectionHistory.push(clearedState);
      
      // Move index to the new state
      state.colorHistoryIndex++;
      
      // Limit history to 20 entries
      if (state.colorSelectionHistory.length > 20) {
        state.colorSelectionHistory.shift(); // Remove first item
        state.colorHistoryIndex--; // Adjust index
      }
    },

    undoColorSelection(state, action){
      if (state.colorHistoryIndex > 0) {
        state.colorHistoryIndex--;
        state.selectedSlotProducts = JSON.parse(JSON.stringify(state.colorSelectionHistory[state.colorHistoryIndex]));
      }
    },

    redoColorSelection(state, action){
      if (state.colorHistoryIndex < state.colorSelectionHistory.length - 1) {
        state.colorHistoryIndex++;
        state.selectedSlotProducts = JSON.parse(JSON.stringify(state.colorSelectionHistory[state.colorHistoryIndex]));
      }
    },

    // Initialize history when a pattern is selected
    initializeColorHistory(state, action){
      state.colorSelectionHistory = [{}];
      state.colorHistoryIndex = 0;
    }, 

    //------------------------------------

    setApplyMode(state, action){
      state.applyMode = action.payload;
    },

    setActiveTile(state, action){
      state.activeTile = action.payload;
    },

    addTileSelection(state, action){
      state.tileSelections[action.payload.id] = action.payload.productObject;
    },

    updateTilePercentage(state, action){

      if(state.applyMode === "random"){
        var totalBlocked = 0;
        var tileCount = 0;

        for(let tile in state.tileSelections){
          if(state.tileSelections[tile]["locked"] === true){
            totalBlocked += parseInt(state.tileSelections[tile]["percentage"]);
          } 
          else{
            tileCount++;
          }
        }

        let calcPercent = ((100-totalBlocked-parseInt(action.payload.percentage))/(tileCount-1)).toFixed(0);

        for(let tile in state.tileSelections){
          if(state.tileSelections[tile]["locked"] === true){
            // leave as is
          }
          else if(state.tileSelections[tile]["id"] === action.payload.id){
            state.tileSelections[tile]["percentage"] = action.payload.percentage;
          } 
          else{
            state.tileSelections[tile]["percentage"] = calcPercent;
          }
        }
        
        console.log(state.tileSelections);

      }
      else{
        // Calculate percentages
        var totPolygons = document.querySelectorAll("#svg-container polygon").length;

        for(const tile in state.tileSelections){
            var polyCount = document.querySelectorAll(`#svg-container polygon[fill*='${tile}']`).length;
            var pcnt = ((polyCount/totPolygons)*100).toFixed(0);

            //console.log("PCNT", polyCount, totPolygons, pcnt)

            state.tileSelections[tile]["percentage"] = pcnt;
        }
      }

    },

    updateTileSelection(state, action) {

      delete state.tileSelections[action.payload.activeTile];
      state.tileSelections[action.payload.id] = action.payload.productObject;

      var totPolygons = document.querySelectorAll("#svg-container polygon").length;

        for(const tile in state.tileSelections){
          var polyCount = document.querySelectorAll(`#svg-container polygon[fill*='${tile}']`).length;
          var pcnt = ((polyCount/totPolygons)*100).toFixed(0);
          state.tileSelections[tile]["percentage"] = pcnt;
      }

    },
  

    removeTile(state, action) {
      // Get the applyMode and id from the action payload
      delete state.tileSelections[action.payload];

      if(state.tileSelections.length === 0) state.activeTile = -1;
   },

   updateTileState(state, action) {
    state.tileSelections[action.payload.tile]["locked"] = action.payload.state;
 },
  
    clearTileSelections(state, action){
      state.tileSelections = {};
      state.activeTile = -1;
      state.applyMode = "flood";
    },

    //------------------------------------
    setProductDataBySeriesId(state, action){
      state.productDataBySeriesId[action.payload.seriesId] = action.payload.data;
    }, 

  //FAVORITES
  initProductFavorites(state, action) {
    //init favorites based on whats in local storage if and only if favorites is empty
    //this gets call on the ready event
    state.favorites = Object.keys(state.favorites || {}).length === 0 ? JSON.parse(localStorage.getItem('favorites')) || {} : state.favorites;

  },
  
  addProductFavorite(state, action) {
      const { key, value } = action.payload;
      if (!state.favorites) {
          state.favorites = {};
      }
      state.favorites[key] = value;
      localStorage.setItem('favorites', JSON.stringify(state.favorites));
  },
  
  removeProductFavorite(state, action) {
      const key = action.payload;
      if (state.favorites && state.favorites[key]) {
          delete state.favorites[key];
          localStorage.setItem('favorites', JSON.stringify(state.favorites));
      }
  },
  

    //-----------------------------------

    // Set share data to be used from social share options & email
    setShareData(state,action){
      state.shareData = action.payload;
    }

  },
});


const initialUIState = {
  vizLoading: false,
  popUpModalActive : false,
  shareModalActive : false,
  helpModalActive : false,
  feedbackModalActive : false,
  emailModalActive : false,
  sceneModalActive:false,
  PatternSizeSelectorModalActive : false,
  productZoomModalActive : false,
  productLimitModalActive : false,
  viewInRoomModalActive : false,
  visualizerHelpModalActive : false,
  guidelines: false,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState: initialUIState,
  reducers: {

    setVizLoading(state, action) {
      state.vizLoading = action.payload;
    },
    setShareModalActive(state, action){
      state.shareModalActive = action.payload;
    },
    setHelpModalActive(state, action){
      state.helpModalActive = action.payload;
    },
    setFeedbackModalActive(state, action){
      state.feedbackModalActive = action.payload;
    },
    setPopUpModalActive(state, action){
      state.popUpModalActive = action.payload;
    },
    setEmailModalActive(state, action){
      state.emailModalActive = action.payload;
    },
    setSceneModalActive(state, action){
      state.sceneModalActive = action.payload;
    },
    setPatternSizeSelectorModalActive(state, action){
      state.PatternSizeSelectorModalActive = action.payload;
    },
    setproductZoomModalActive(state, action){
      state.productZoomModalActive = action.payload;
    },
    setProductLimitModalActive(state, action){
      state.productLimitModalActive = action.payload;
    },
    setViewInRoomModalActive(state, action){
      state.viewInRoomModalActive = action.payload;
    },
    setVisualizerHelpModalActive(state, action){
      state.visualizerHelpModalActive = action.payload;
    },
    setGuidelines(state, action){
      state.guidelines = action.payload;
    }
  },
});

const store = configureStore({
  reducer: {
    visualizer: visualizerSlice.reducer,
    ui: uiSlice.reducer
  }
});

export const visualizerActions = visualizerSlice.actions;
export const uiActions = uiSlice.actions;

export default store;